export const TIME_DISCOUNT_TIERS = [
  { minHoursBefore: 168, discount: 50 },
  { minHoursBefore: 120, discount: 40 },
  { minHoursBefore: 72, discount: 30 },
  { minHoursBefore: 48, discount: 20 },
  { minHoursBefore: 24, discount: 10 },
  { minHoursBefore: 0, discount: 0 },
] as const;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function safeNumber(value: number | string | null | undefined, fallback = 0) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function getScheduleDiscountCap(capacity: number) {
  if (capacity <= 12) return 20000;
  if (capacity <= 24) return 50000;
  return 100000;
}

function getBookingOrderDiscountPercentage(capacity: number, bookingNumber: number) {
  if (capacity < 12) return 0;
  if (bookingNumber > capacity) return 0;
  if (capacity <= 12) {
    if (bookingNumber === 1) return 20;
    return 10;
  }
  if (capacity <= 24) {
    if (bookingNumber <= 2) return 30;
    if (bookingNumber <= 4) return 20;
    return 10;
  }

  if (bookingNumber === 1) return 50;
  if (bookingNumber === 2) return 40;
  if (bookingNumber <= 4) return 30;
  if (bookingNumber <= 6) return 20;
  return 10;
}

function calculateProjectedDiscountAmount(normalPrice: number, capacity: number, bookingCount: number) {
  const projectedBookingCount = Math.min(bookingCount, capacity);
  let projectedDiscountAmount = 0;

  for (let bookingNumber = 1; bookingNumber <= projectedBookingCount; bookingNumber += 1) {
    const discountPercentage = getBookingOrderDiscountPercentage(capacity, bookingNumber);
    projectedDiscountAmount += Math.round(normalPrice * (discountPercentage / 100));
  }

  return projectedDiscountAmount;
}

function getJakartaDateKey(date: Date) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function getTimeDiscountPercentage({ scheduleStartAt, bookingAt }: { scheduleStartAt: Date | string; bookingAt: Date | string; }) {
  const scheduleDate = scheduleStartAt instanceof Date ? scheduleStartAt : new Date(scheduleStartAt);
  const bookingDate = bookingAt instanceof Date ? bookingAt : new Date(bookingAt);

  if (Number.isNaN(scheduleDate.getTime()) || Number.isNaN(bookingDate.getTime())) {
    return 0;
  }

  const hoursBefore = (scheduleDate.getTime() - bookingDate.getTime()) / (1000 * 60 * 60);
  if (hoursBefore <= 0) {
    return 0;
  }

  if (hoursBefore < 24 && getJakartaDateKey(scheduleDate) !== getJakartaDateKey(bookingDate)) {
    return 10;
  }

  for (const tier of [...TIME_DISCOUNT_TIERS].sort((a, b) => b.minHoursBefore - a.minHoursBefore)) {
    if (hoursBefore >= tier.minHoursBefore) {
      return tier.discount;
    }
  }

  return 0;
}

export function calculateBookingPrice({
  normalPrice,
  capacity,
  currentParticipants,
  bookingCount = currentParticipants,
  accumulatedDiscountAmount = 0,
  scheduleStartAt,
  bookingAt,
}: {
  normalPrice: number | string;
  capacity: number | string;
  currentParticipants: number | string;
  bookingCount?: number | string;
  accumulatedDiscountAmount?: number | string;
  scheduleStartAt: Date | string;
  bookingAt: Date | string;
}) {
  const safeNormalPrice = Math.max(safeNumber(normalPrice, 0), 0);
  const safeCapacity = Math.max(safeNumber(capacity, 1), 1);
  const safeCurrentParticipants = clamp(safeNumber(currentParticipants, 0), 0, safeCapacity);
  const safeBookingCount = Math.max(Math.floor(safeNumber(bookingCount, safeCurrentParticipants)), 0);
  const safeAccumulatedDiscountAmount = Math.max(safeNumber(accumulatedDiscountAmount, 0), 0);
  const projectedAccumulatedDiscountAmount = calculateProjectedDiscountAmount(safeNormalPrice, safeCapacity, safeBookingCount);
  const capUsageBeforeBooking = Math.max(safeAccumulatedDiscountAmount, projectedAccumulatedDiscountAmount);

  const occupancyPercentage = (safeCurrentParticipants / safeCapacity) * 100;
  const remainingPercentage = ((safeCapacity - safeCurrentParticipants) / safeCapacity) * 100;
  const timeDiscountPercentage = getTimeDiscountPercentage({ scheduleStartAt, bookingAt });
  const bookingOrderDiscountPercentage = getBookingOrderDiscountPercentage(safeCapacity, safeBookingCount + 1);
  const baseDiscountPercentage = Math.min(timeDiscountPercentage, bookingOrderDiscountPercentage);
  const scheduleDiscountCap = getScheduleDiscountCap(safeCapacity);
  let finalDiscountPercentage = baseDiscountPercentage;
  let discountAmount = Math.round(safeNormalPrice * (finalDiscountPercentage / 100));
  if (capUsageBeforeBooking + discountAmount > scheduleDiscountCap) {
    finalDiscountPercentage = 0;
    discountAmount = 0;
  }
  const finalPrice = Math.max(safeNormalPrice - discountAmount, 0);

  return {
    normalPrice: safeNormalPrice,
    timeDiscountPercentage,
    bookingOrderDiscountPercentage,
    occupancyDiscountPercentage: bookingOrderDiscountPercentage,
    finalDiscountPercentage,
    discountAmount,
    finalPrice,
    occupancyPercentage,
    remainingPercentage,
    scheduleDiscountCap,
    accumulatedDiscountAmount: capUsageBeforeBooking,
    remainingDiscountBudget: Math.max(scheduleDiscountCap - capUsageBeforeBooking - discountAmount, 0),
    timeUntilSchedule: Math.max((new Date(scheduleStartAt).getTime() - new Date(bookingAt).getTime()) / (1000 * 60 * 60), 0),
  };
}
